import React, { useState } from "react";
import { Loader2, Images, StopCircle } from "lucide-react";
import { api } from "@/lib/api";
import { ACTIVE, JobCard, useJobPoll } from "./ReimageJobBits";

function Plan({ plan, busy, onStart, onCancel }) {
  return (
    <div data-testid="reimage-plan" className="border border-zinc-800 rounded-sm p-3 space-y-2 text-xs">
      <div className="text-zinc-200">
        <span className="mono text-amber-300">{plan.total}</span> article(s) across <span className="mono">{plan.sites}</span> site(s) will get a new
        {plan.pexels_configured ? " stock photo (or a branded card when none fits)" : " branded card (no Pexels key → cards only)"}. Copied source photos are removed from R2.
      </div>
      <div className="text-zinc-500 mono">Pexels quota this hour {plan.quota?.hour_used}/{plan.quota?.hourly_max} · month {plan.quota?.month_used}/{plan.quota?.monthly_max}. Beyond quota the job falls back to cards automatically.</div>
      {plan.targets?.length > 1 && (
        <div className="max-h-32 overflow-auto divide-y divide-zinc-900 border border-zinc-900 rounded-sm">
          {plan.targets.filter((t) => t.articles).map((t) => (
            <div key={t.site_id} className="flex justify-between px-2 py-1 mono text-[11px]"><span className="text-zinc-300 truncate">{t.domain || t.name}</span><span className="text-zinc-500">{t.policy} · {t.articles}</span></div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" data-testid="reimage-start-btn" disabled={busy || !plan.total} onClick={onStart} className="px-3 py-1.5 text-xs font-semibold bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-40 rounded-sm">
          {plan.total ? `Start re-imaging ${plan.total}` : "Nothing to re-image"}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-xs border border-zinc-800 rounded-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
      </div>
    </div>
  );
}

/** Dry-run → confirm → background job with live progress. `siteIds` empty = whole network. */
export default function ReimagePanel({ siteIds = [], force = false, onDone }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { job, setJob, track } = useJobPoll(onDone);
  const key = siteIds.join(",");
  const running = ACTIVE.includes(job?.status);

  async function call(dryRun) {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/images/reimage", { site_ids: siteIds, force, dry_run: dryRun });
      if (dryRun) { setJob(null); setPlan(r.data); }
      else { setPlan(null); track(r.data.job_id, r.data.total); }
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  async function cancel() {
    try { await api.post(`/images/jobs/${job.id}/cancel`); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  }

  return (
    <div data-testid={`reimage-panel-${key || "network"}`} className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button" data-testid="reimage-preview-btn" disabled={busy || running} onClick={() => call(true)}
          className="px-3 py-1.5 text-xs border border-zinc-700 rounded-sm hover:border-zinc-400 disabled:opacity-50 flex items-center gap-1.5"
        >{busy && !plan ? <Loader2 size={12} className="animate-spin" /> : <Images size={12} />} {siteIds.length ? "Re-image this site…" : "Re-image all published articles…"}</button>
        {running && (
          <button type="button" data-testid="reimage-cancel-btn" onClick={cancel} className="px-3 py-1.5 text-xs border border-red-800 text-red-300 rounded-sm hover:border-red-500 flex items-center gap-1.5">
            <StopCircle size={12} /> Stop
          </button>
        )}
      </div>
      {plan && <Plan plan={plan} busy={busy} onStart={() => call(false)} onCancel={() => setPlan(null)} />}
      {job && <JobCard job={job} />}
      {err && <div data-testid="reimage-error" className="text-xs text-red-400 border border-red-900/60 bg-red-950/40 px-3 py-2 rounded-sm font-mono">{err}</div>}
    </div>
  );
}
